import React, { useEffect, useState, useRef, useCallback } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import InfoIcon from "../support_files/InfoIcon";
import HelpIcon from "../support_files/HelpIcon";
import ToggleButton from '@mui/material/ToggleButton';

import HeightIcon from '@mui/icons-material/Height';
import CancelIcon from '@mui/icons-material/Cancel';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import TocIcon from '@mui/icons-material/Toc';
import SourceIcon from '@mui/icons-material/Source';

import Fade from '@mui/material/Fade';

import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';

import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { ResponsiveBar } from '@nivo/bar'
import { ResponsiveLine } from '@nivo/line'

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import heightData from '../info_data/heightInfoData.json';

import { createTheme, ThemeProvider } from '@mui/material/styles';

import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

export default function HeightInfoDrawer({
  selectedInfo, setSelectedInfo, isMetric
}) {

  const races = ['White', 'Hispanic', 'Black', 'Asian', 'All'];
  
  // Use ref to directly manipulate DOM classes - no re-renders
  const headerRef = useRef(null);
  const handleScroll = useCallback((event) => {
    const scrollTop = event.target.scrollTop;
    if (headerRef.current) {
      if (scrollTop > 20) {
        headerRef.current.classList.add('header-translucent');
        headerRef.current.classList.remove('header-solid');
      } else {
        headerRef.current.classList.add('header-solid');
        headerRef.current.classList.remove('header-translucent');
      }
    }
  }, []); // Empty dependency array since we're using refs

  // Helper function to format height values
  const formatHeight = (value) => {
    if (isMetric) {
      return `${value.toFixed(0)}cm`;
    } else {
      // Round the total inches first, then convert
      const totalInches = Math.floor(value);
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return `${feet}'${inches}"`;
    }
  };

  // Memoize chart components to prevent unnecessary re-renders
  const HeightByGenderLine = (({ gender }) => {
    const unit = isMetric ? 'cm' : 'ft/in';
    const yAxisLabel = `Height (${unit})`;
    const measurementSystem = isMetric ? 'metric' : 'imperial';
    
    const [animatedData, setAnimatedData] = useState([]);
    const [isAnimating, setIsAnimating] = useState(true);
    
    // Create data array with all races for the specified gender
    const actualData = races.map(race => {
      const raceData = heightData[measurementSystem][race];
      const genderData = raceData.find(item => item.id === gender);
      
      const raceColors = {
        'All': '#666666',
        'White': '#b4e1ff', 
        'Black': '#C5DCA0',
        'Asian': '#ffb7c3',
        'Hispanic': '#ffdab9'
      };
      
      return {
        id: race === 'All' ? 'All Races' : race,
        color: raceColors[race],
        data: genderData.data
      };
    });

    // Initialize with zero values and animate to actual values
    useEffect(() => {
      // Start with zero values
      const zeroData = actualData.map(serie => ({
        ...serie,
        data: serie.data.map(point => ({ ...point, y: 0 }))
      }));
      
      setAnimatedData(zeroData);
      
      // Animate to actual values after a short delay
      const timer = setTimeout(() => {
        setAnimatedData(actualData);
        setIsAnimating(false);
      }, 200);
      
      return () => clearTimeout(timer);
    }, []);

    const data = animatedData;

    return (
      <ResponsiveLine
        data={data}
        height={320}
        margin={{ top: 30, right: 30, bottom: 80, left: 60 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false,
          nice: true
        }}
        yFormat={value => formatHeight(value)}
        curve="cardinal"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Percentile',
          legendOffset: 36,
          legendPosition: 'middle'
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: yAxisLabel,
          legendOffset: -55,
          legendPosition: 'middle',
          format: value => formatHeight(value)
        }}
        pointSize={8}
        pointColor="#ffffff"
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabelYOffset={-12}
        enableTouchCrosshair={true}
        useMesh={true}
        colors={(serie) => {
          const raceColors = {
            'All Races': '#666666',
            'White': '#b4e1ff', 
            'Black': '#C5DCA0',
            'Asian': '#ffb7c3',
            'Hispanic': '#ffdab9'
          };
          return raceColors[serie.id] || '#666666';
        }}
        lineWidth={3}
        enablePointLabel={false}
        animate={true}
        motionConfig="wobbly"
        tooltip={({ point }) => (
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.85)',
              color: '#ffffff',
              fontSize: '14px',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
              padding: '4px 6px',
              border: `2px solid ${point.serieColor}`
            }}
          >
            <div style={{ fontSize: '10px', fontWeight: 600, marginBottom: '2px' }}>
              {point.serieId}
            </div>
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              {point.data.x}
            </div>
            <div style={{ fontSize: '10px', fontWeight: 500 }}>
              {formatHeight(point.data.y)}
            </div>
          </div>
        )}
        theme={{
          background: 'transparent',
          text: {
            fill: '#ffffff',
            fontSize: 11
          },
          axis: {
            domain: {
              line: {
                stroke: '#777777',
                strokeWidth: 1
              }
            },
            legend: {
              text: {
                fill: '#ffffff',
                fontSize: 12,
                fontWeight: 500
              }
            },
            ticks: {
              line: {
                stroke: '#777777',
                strokeWidth: 1
              },
              text: {
                fill: '#ffffff',
                fontSize: 11
              }
            }
          },
          grid: {
            line: {
              stroke: '#444444',
              strokeWidth: 1,
              strokeDasharray: '2 2'
            }
          },
          crosshair: {
            line: {
              stroke: '#ffffff',
              strokeWidth: 1,
              strokeOpacity: 0.35
            }
          }
        }}
        legends={[
          {
            anchor: 'bottom',
            direction: 'row',
            translateX: 0,
            translateY: 65,
            itemsSpacing: 20,
            itemDirection: 'left-to-right',
            itemWidth: 45,
            itemHeight: 18,
            itemOpacity: 0.85,
            symbolSize: 8,
            symbolSpacing: 4,
            symbolShape: 'circle',
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            itemTextColor: '#ffffff',
            effects: [
              {
                on: 'hover',
                style: {
                  itemBackground: 'rgba(0, 0, 0, .1)',
                  itemOpacity: 1
                }
              }
            ]
          }
        ]}
      />
    );
  });

  const HeightLine = (({ race }) => {
    const unit = isMetric ? 'cm' : 'ft/in';
    const yAxisLabel = `Height (${unit})`;
    const actualData = heightData[isMetric ? 'metric' : 'imperial'][race];
    
    const [animatedData, setAnimatedData] = useState([]);
    const [isAnimating, setIsAnimating] = useState(true);

    // Initialize with zero values and animate to actual values
    useEffect(() => {
      // Start with zero values
      const zeroData = actualData.map(serie => ({
        ...serie,
        data: serie.data.map(point => ({ ...point, y: 0 }))
      }));
      
      setAnimatedData(zeroData);
      
      // Animate to actual values after a short delay
      const timer = setTimeout(() => {
        setAnimatedData(actualData);
        setIsAnimating(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }, [race, isMetric]);

    const data = animatedData;

    return (
      <ResponsiveLine
        data={data}
        height={320}
        margin={{ top: 30, right: 30, bottom: 80, left: 60 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false,
          nice: true
        }}
        yFormat={value => formatHeight(value)}
        curve="cardinal"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Percentile',
          legendOffset: 36,
          legendPosition: 'middle'
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: yAxisLabel,
          legendOffset: -55,
          legendPosition: 'middle',
          format: value => formatHeight(value)
        }}
        pointSize={8}
        pointColor="#ffffff"
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabelYOffset={-12}
        enableTouchCrosshair={true}
        useMesh={true}
        colors={(serie) => {
          const colorMap = {
            'Female': '#ff9b69',
            'Male': '#3dbce3'
          };
          return colorMap[serie.id] || '#666666';
        }}
        lineWidth={3}
        enablePointLabel={false}
        animate={true}
        motionConfig="wobbly"
        tooltip={({ point }) => (
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.85)',
              color: '#ffffff',
              fontSize: '14px',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
              padding: '4px 6px',
              border: `2px solid ${point.serieColor}`
            }}
          >
            <div style={{ fontSize: '10px', fontWeight: 600, marginBottom: '2px' }}>
              {point.serieId}
            </div>
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              {point.data.x} percentile
            </div>
            <div style={{ fontSize: '10px', fontWeight: 500 }}>
              {formatHeight(point.data.y)}
            </div>
          </div>
        )}
        theme={{
          background: 'transparent',
          text: {
            fill: '#ffffff',
            fontSize: 11
          },
          axis: {
            domain: {
              line: {
                stroke: '#777777',
                strokeWidth: 1
              }
            },
            legend: {
              text: {
                fill: '#ffffff',
                fontSize: 12,
                fontWeight: 500
              }
            },
            ticks: {
              line: {
                stroke: '#777777',
                strokeWidth: 1
              },
              text: {
                fill: '#ffffff',
                fontSize: 11
              }
            }
          },
          grid: {
            line: {
              stroke: '#444444',
              strokeWidth: 1,
              strokeDasharray: '2 2'
            }
          },
          crosshair: {
            line: {
              stroke: '#ffffff',
              strokeWidth: 1,
              strokeOpacity: 0.35
            }
          }
        }}
        legends={[
          {
            anchor: 'bottom',
            direction: 'row',
            translateX: 0,
            translateY: 65,
            itemsSpacing: 20,
            itemDirection: 'left-to-right',
            itemWidth: 45,
            itemHeight: 18,
            itemOpacity: 0.85,
            symbolSize: 8,
            symbolSpacing: 4,
            symbolShape: 'circle',
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            itemTextColor: '#ffffff',
            effects: [
              {
                on: 'hover',
                style: {
                  itemBackground: 'rgba(0, 0, 0, .1)',
                  itemOpacity: 1
                }
              }
            ]
          }
        ]}
      />
    );
  });

  const HeightTable = React.memo(({ data }) => {
    const handleTouchStart = (e) => {
      e.stopPropagation();
    };

    const handleTouchMove = (e) => {
      e.stopPropagation();
    };

    // Get all unique percentiles from the data
    const percentiles = data[0]?.data.map(point => point.x) || [];

    return (
      <TableContainer
        component={Paper}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        sx={{
          overflow: 'auto',
          marginBottom: '140px',
          borderRadius: '12px',
          backgroundColor: '#1a1a1a',
          '& .MuiTable-root': {
            '& .MuiTableCell-root': {
              color: 'white',
              borderBottom: '1px solid #444'
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: '#333'
            }
          }
        }}
      >
        <Table sx={{ maxWidth: 500 }} size="small" aria-label="height percentiles table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  width: '60px',
                  height: '60px',
                  padding: 0,
                  backgroundColor: '#2d2d2d',
                  zIndex: 3,
                  borderRight: '1px solid #444',
                  borderBottom: '1px solid #444',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#2d2d2d',
                  }}
                >
                  {/* Diagonal line */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(to bottom left, transparent 49%, #444 50%, transparent 20%)',
                      zIndex: 1,
                    }}
                  />

                  {/* Top-left label */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 6,
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: 'white',
                      zIndex: 2,
                    }}
                  >
                    Percentile
                  </div>

                  {/* Bottom-right label */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      left: 6,
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: 'white',
                      zIndex: 2,
                    }}
                  >
                    Race
                  </div>
                </div>
              </TableCell>

              {percentiles.map((percentile) => (
                <TableCell key={percentile} align="right" sx={{ width: '45px', lineHeight: '15px', fontSize: '0.75rem' }}>
                  {percentile}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((race) => (
              <TableRow
                key={race.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    width: '50px',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#2d2d2d',
                    zIndex: 1,
                    boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
                    fontWeight: 500,
                    padding: '6px 10px 6px 10px',
                    fontSize: '0.75rem'
                  }}
                >
                  {race.id === 'All' ? 'All Races' : race.id}
                </TableCell>
                {race.data.map((dataPoint) => (
                  <TableCell
                    key={dataPoint.x}
                    align="right"
                    sx={{ width: '50px', fontSize: '0.75rem' }}
                  >
                    {formatHeight(dataPoint.y)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  });

  const drawerContent = () => (
    <div className="drawer-container" style={{ overflow: 'auto' }} onScroll={handleScroll}>
      <div ref={headerRef} className="sticky-header header-solid">
        <div className="header-content">
          <div className="header-left">
            <HeightIcon />
            <h2 className="header-title">Height</h2>
          </div>
          <div className="header-right">
            <button
              onClick={() => { setSelectedInfo(false) }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <CancelIcon sx={{ fill: "#b4b4b4ff", fontSize: "28px" }} />
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <InsertChartIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Summary</h3>
            </div>
            <div style={{height: '360px'}}>
              <Swiper
                pagination={true}
                navigation={true}
                className="mySwiper"
                touchStartPreventDefault={false}
                preventClicks={false}
                preventClicksPropagation={false}
                allowSlidePrev={true}
                allowSlideNext={true}
                simulateTouch={true}
                touchMoveStopPropagation={false}
                modules={[Pagination, Navigation]}
                style={{
                  '--swiper-navigation-color': 'rgba(255, 255, 255, 0.2)',
                  '--swiper-navigation-size': '22px',
                  height: '360px'
                }}
              >
                <SwiperSlide>
                  <div className="section-header" style={{ marginBottom: "0", justifyContent: "center" }}>
                    <h3 className="section-title" style={{ fontWeight: 600 }}>Women</h3>
                  </div>
                  <div style={{height: '400px'}}>
                    <HeightByGenderLine gender="Female" />
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="section-header" style={{ marginBottom: "0", justifyContent: "center" }}>
                    <h3 className="section-title" style={{ fontWeight: 600 }}>Men</h3>
                  </div>
                  <div style={{height: '400px'}}>
                    <HeightByGenderLine gender="Male" />
                  </div>
                </SwiperSlide>
                {races.slice().reverse().map(race => (
                  <SwiperSlide key={race}>
                    <div className="section-header" style={{ marginBottom: "0", justifyContent: "center" }}>
                      <h3 className="section-title" style={{ fontWeight: 600 }}>
                        {race === 'All' ? 'All Races' : race}
                      </h3>
                    </div>
                    <div style={{height: '400px'}}>
                      <HeightLine race={race} />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <p className="card-text">
              These charts display the height distribution percentiles for adults in the US, categorized by gender and race according to the CDC.¹.
              <br/>
              For example, a data point from the chart shows that 85% of men are 6 feet tall or shorter, meaning only 15% of men are taller than 6 feet.
            </p>
          </div>
        </div>

        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <TocIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Details</h3>
            </div>
            <div className="visual-container">
              <Swiper
                pagination={true}
                navigation={true}
                className="mySwiper"
                touchStartPreventDefault={false}
                preventClicks={false}
                preventClicksPropagation={false}
                allowSlidePrev={true}
                allowSlideNext={true}
                simulateTouch={true}
                touchMoveStopPropagation={false}
                modules={[Pagination, Navigation]}
                style={{
                  '--swiper-navigation-color': 'rgba(255, 255, 255, 0.2)',
                  '--swiper-navigation-size': '22px',
                }}
              >
                <SwiperSlide>
                  <div className="section-header" style={{ marginBottom: "25px", justifyContent: "center" }}>
                    <h3 className="section-title" style={{ fontWeight: 600 }}>Women</h3>
                  </div>
                  <div style={{height: '260px' , marginBottom: '20px'}}>
                    <HeightTable data={races.slice().reverse().map(race => {
                      const raceData = heightData[isMetric ? 'metric' : 'imperial'][race];
                      const femaleData = raceData.find(item => item.id === 'Female');
                      return {
                        id: race,
                        data: femaleData.data
                      };
                    })} />
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="section-header" style={{ marginBottom: "25px", justifyContent: "center" }}>
                    <h3 className="section-title" style={{ fontWeight: 600 }}>Men</h3>
                  </div>
                  <div style={{height: '260px', marginBottom: '40px' }}>
                    <HeightTable data={races.slice().reverse().map(race => {
                      const raceData = heightData[isMetric ? 'metric' : 'imperial'][race];
                      const maleData = raceData.find(item => item.id === 'Male');
                      return {
                        id: race,
                        data: maleData.data
                      };
                    })} />
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
            <p className="card-text">
              
            </p>
          </div>
        </div>

        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <SourceIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Source</h3>
            </div>
            <div className="visual-container">
            </div>
            <p className="card-text">
              [1] CDC, 2021 National Health and Nutrition Examination Survey
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <SwipeableDrawer
        anchor="bottom"
        open={selectedInfo}
        onClose={() => setSelectedInfo(false)}
        onOpen={() => setSelectedInfo(true)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: 'rgba(7, 28, 52, 1)',
            color: "white",
            borderTopRightRadius: '25px 15px',
            borderTopLeftRadius: '25px 15px',
            height: 'calc(100dvh - 95px)',
          },
        }}
        // slotProps={{
        //   paper: {
        //                 onScroll: handleScroll

        //   }
        // }}
      >
        <div className="additionalInfoContainer">
          {drawerContent()}
        </div>
      </SwipeableDrawer>
    </div>
  )
}